import type { ComponentProps } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DayPicker } from 'react-day-picker'

import { cn } from '@/utils/cn'
import { buttonVariants } from '@/components/ui/button-variants'

export type CalendarProps = ComponentProps<typeof DayPicker>

const navBtn = cn(
  buttonVariants({ variant: 'ghost' }),
  'h-7 w-7 bg-transparent p-0 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100',
)
const day = cn(
  buttonVariants({ variant: 'ghost' }),
  'h-9 w-9 p-0 font-normal text-zinc-200 hover:bg-zinc-800 hover:text-zinc-100 aria-selected:opacity-100',
)
const cell =
  'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-violet-950/40 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20'

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium text-zinc-100',
        nav: 'space-x-1 flex items-center',
        nav_button: navBtn,
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell: 'text-zinc-500 rounded-md w-9 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell,
        day,
        day_selected:
          'bg-violet-600 text-white hover:bg-violet-600 hover:text-white focus:bg-violet-600 focus:text-white',
        day_today: 'bg-zinc-800 text-zinc-100',
        day_outside:
          'text-zinc-600 opacity-50 aria-selected:bg-violet-950/30 aria-selected:text-zinc-400',
        day_disabled: 'text-zinc-600 opacity-30',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className='h-4 w-4' />,
        IconRight: () => <ChevronRight className='h-4 w-4' />,
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
